// アプリ全体で 1 つだけ持つ共有インスタンス。UI 層はここから import し、
// src/lib/** を直接組み立てない（CLAUDE.md、docs/spec/sandboxd-client.md §4.2、§4.7 方式 A）。
import lessonDocs from 'virtual:lessons';
import problemDocs from 'virtual:problems';
import { buildCatalog, parseContentYaml, type CardCatalog } from './lib/cards';
import { buildLessonIndex, type LessonIndex } from './lib/lessons';
import { buildProblemIndex, type ProblemIndex } from './lib/problems';
import {
  createMemoryStorage,
  createStore,
  type ProgressStore,
  type StorageLike,
} from './lib/progress';
import { createSandboxClient, createSha256Hex, type SandboxClient } from './lib/sandboxd';
import { currentRunSpan, tracedFetch } from './lib/telemetry';

// content 配下の全 YAML をビルドに取り込み、カタログにする（use-cases.yaml も含む）。
const contentFiles = import.meta.glob('/content/**/*.yaml', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export const catalog: CardCatalog = buildCatalog(
  Object.entries(contentFiles).map(([path, text]) => parseContentYaml(text, path)),
);

/**
 * レッスン（content/lessons/**\/*.md）。ビルド時に vite.config.ts の lessonsPlugin が LessonDoc[] にしたもの
 * （docs/spec/lesson-format.md §5.1。ブラウザでは Markdown を解析しない）。ユニットの並びはカードと同じ。
 */
export const lessons: LessonIndex = buildLessonIndex(lessonDocs, {
  unitOrder: catalog.units.map((u) => u.unit),
});

/**
 * 問題（content/problems/*.md）。ビルド時に vite.config.ts の problemsPlugin が ProblemDoc[] にしたもの
 * （docs/spec/problem-format.md §5.1）。一覧は order 順で draft を除く。手法・文法・レッスン・ユニット別の索引付き。
 */
export const problems: ProblemIndex = buildProblemIndex(problemDocs);

/** localStorage が使えない環境（読むだけで例外を投げるブラウザ設定）ではメモリ上で続ける。 */
function browserStorage(): StorageLike {
  try {
    if (globalThis.localStorage) return globalThis.localStorage;
  } catch {
    // 下のメモリ実装にする
  }
  return createMemoryStorage();
}

/**
 * 進捗ストア（localStorage。テストでは createStore に別の storage を渡す）。
 * 読み込み（load）は src/main.ts が mount の前に 1 回行う。formerIds の移し替えにカードを渡す。
 */
/** 端末の時計。日付は toLocalDate で端末ローカルの暦日にする（progress-store.md §2）。 */
export const clock = (): Date => new Date();

export const progress: ProgressStore = createStore(browserStorage(), clock, {
  cards: catalog.cards,
});

/** sandboxd クライアント。traceparent は tracedFetch が現在の class.run / class.fmt span から付ける。 */
export const sandbox: SandboxClient = createSandboxClient({
  fetch: (input, init) => tracedFetch(input, init, currentRunSpan()),
  sha256Hex: createSha256Hex(),
  sleep: (ms, signal) =>
    new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, ms);
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          reject(signal.reason instanceof Error ? signal.reason : new Error('aborted'));
        },
        { once: true },
      );
    }),
  random: Math.random,
});
